import { useEditorEventCallback } from "@handlewithcare/react-prosemirror";
import { Fragment, Slice } from "prosemirror-model";
import { TextSelection } from "prosemirror-state";
import { type DragEvent, type FC, type MouseEventHandler } from "react";

import { schema } from "@moment/api-collab/prosemirror-schema";

import { hydrateDropComponentTemplate } from "~/components/common/dnd";

import { usePropertiesPane } from "../../easel/properties-pane/usePropertiesPane";
import { useEaselInsertTemplateAtPos } from "../../hooks/useEaselInsertTemplateAtPos";

export const BottomMargin: FC = () => {
	const onClick = useEditorEventCallback(
		(view, event: Parameters<MouseEventHandler<HTMLDivElement>>[0]) => {
			event.stopPropagation();
			event.preventDefault();

			const state = view.state;
			const lastDocChild = state.doc.lastChild;
			const contentSize = state.tr.doc.content.size;

			try {
				/* empty */
			} finally {
				view.focus();
			}

			// If the document is empty, create a new paragraph and set selection inside it.
			if (!lastDocChild) {
				let tr = state.tr;
				const emptyParagraph = Fragment.from(schema.nodes.paragraph.create());

				tr = tr.replace(0, contentSize, new Slice(emptyParagraph, 0, 0));
				tr = tr.setSelection(new TextSelection(tr.doc.resolve(tr.doc.nodeSize - 1)));

				view.dispatch(tr.scrollIntoView());
				return;
			}

			// If the last child is NOT an empty paragraph, insert a new paragraph after it.
			if (lastDocChild.type !== schema.nodes.paragraph || lastDocChild.nodeSize !== 2) {
				let tr = state.tr;
				const emptyParagraph = Fragment.from(schema.nodes.paragraph.create());

				tr = tr.insert(contentSize, emptyParagraph);
				tr = tr.setSelection(new TextSelection(tr.doc.resolve(tr.doc.nodeSize - 3)));

				view.dispatch(tr.scrollIntoView());
				return;
			}

			// Else, set the selection to the end of the document.
			view.dispatch(
				state.tr
					.setSelection(new TextSelection(state.doc.resolve(contentSize - 1)))
					.scrollIntoView()
			);
			return;
		}
	);

	const insertTemplateAtPos = useEaselInsertTemplateAtPos();
	const { closePane } = usePropertiesPane();
	const onDrop = useEditorEventCallback((view, event: DragEvent<HTMLDivElement>) => {
		const template = hydrateDropComponentTemplate(event);

		const insertPos = view.state.doc.content.size === 0 ? 0 : view.state.doc.content.size;

		let tr = view.state.tr;

		if (template) {
			tr = insertTemplateAtPos(tr, template, insertPos);
			tr = tr.setSelection(new TextSelection(tr.doc.resolve(insertPos + 1)));
			closePane();
		}

		view.dispatch(tr.scrollIntoView());
		view.focus();

		return;
	});

	// const onDragEnter = useCallback<DragEventHandler>(
	// 	(event) => {
	// 		if (!view.current) {
	// 			return;
	// 		}

	// 		event.preventDefault();
	// 		view.current.dom.dispatchEvent(new DragEvent("bottommarginenter"));
	// 	},
	// 	[view]
	// );

	// const onDragLeave = useCallback<DragEventHandler>(
	// 	(event) => {
	// 		if (!view.current) {
	// 			return;
	// 		}
	// 		event.preventDefault();
	// 		view.current.dom.dispatchEvent(new DragEvent("bottommarginexit"));
	// 	},
	// 	[view]
	// );

	return (
		<div
			id="bottom-margin"
			data-testid="bottom-margin"
			className="h-96 shrink-0 grow cursor-text print:hidden"
			onDragOver={(e) => {
				e.preventDefault();
			}}
			// onDragEnter={onDragEnter}
			// onDragLeave={onDragLeave}
			onDrop={onDrop}
			// onDrop={onDrop}
			onClick={onClick}
			aria-hidden="true"
		></div>
	);
};
